import { openDialog, closeLatestDialog } from '@patrianna/dialog-manager'
import { buyShopOffer } from '@patrianna-payments/shared-store/payments/store/middleware-actions'
import type { OfferInfo } from '@patrianna-payments/shared-store/payments/types/openApiModels/OfferInfo'
import { usePathname, useRouter, useSearchParams } from 'next/navigation'

import ROUTES from 'config/routes.json'
import { getSnowplowPaymentFlow } from 'src/analyticActions/common/trackPaymentSnowplow'
import { useAppDispatch } from 'store/hooks'
import { redeemFlowStart } from 'store/modules/redeem/actions'
import { getDataFromSessionStorage } from 'utils/sessionStorage'

export type VerificationFromProps = 'redeem' | 'payment' | 'account' | 'sweepstakes-code'

type UseReturnAfterVerificationProps = {
  handleOpenSubmitDocumentsDialogAfterClosingVerificationDialog: () => void
  handleOpenAfterClosingSubmitDocumentsDialog: (verificationFrom?: VerificationFromProps | string) => void
  handleSetVerificationFromToParams: (verificationFrom: VerificationFromProps | string) => void
  handleRemoveVerificationFromParams: () => void
  getVerificationFrom: () => string | null
}

export const OFFER_CODE_VERIFICATION = 'TEMP_OFFER_VERIFICATION'
export const VERIFICATION_FROM_PARAM = 'verificationFrom'

export const useReturnAfterVerification = (): UseReturnAfterVerificationProps => {
  const dispatch = useAppDispatch()
  const { push, replace } = useRouter()
  const searchParams = useSearchParams()
  const pathname = usePathname()

  const getVerificationFrom = (): string | null =>
    new URLSearchParams(searchParams as unknown as URLSearchParams)?.get(VERIFICATION_FROM_PARAM) || null

  const handleSetVerificationFromToParams = (verificationFrom: VerificationFromProps | string) => {
    if (verificationFrom) {
      const newSearchParams = new URLSearchParams(searchParams as unknown as URLSearchParams)

      newSearchParams?.set(VERIFICATION_FROM_PARAM, verificationFrom)
      replace(`${pathname}?${newSearchParams?.toString()}`)
    }
  }

  const handleRemoveVerificationFromParams = () => {
    if (searchParams.has(VERIFICATION_FROM_PARAM)) {
      const newSearchParams = new URLSearchParams(searchParams as unknown as URLSearchParams)

      newSearchParams?.delete(VERIFICATION_FROM_PARAM)

      const query = newSearchParams?.toString()

      replace(query ? `${pathname}?${query}` : pathname)
    }
  }

  const handleOpenSubmitDocumentsDialogAfterClosingVerificationDialog = () => {
    const verificationFrom = getVerificationFrom()

    if ((verificationFrom && verificationFrom !== 'account') || !verificationFrom) {
      return openDialog({ dialogName: 'SUBMIT_DOCUMENTS_DIALOG', dialogProps: { verificationFrom } })
    }

    if (verificationFrom && verificationFrom === 'account') {
      handleRemoveVerificationFromParams()
    }
  }

  const handleOpenAfterClosingSubmitDocumentsDialog = (verificationStartFrom?: VerificationFromProps | string) => {
    const verificationFrom = getVerificationFrom() || verificationStartFrom

    if (verificationFrom) {
      if (verificationFrom === 'redeem') {
        handleRemoveVerificationFromParams()

        return dispatch(redeemFlowStart(push))
      }

      const offerCode = getDataFromSessionStorage<string>(OFFER_CODE_VERIFICATION) // [FYI]: offerCode set up on payment dialog

      if (verificationFrom === 'payment' && offerCode) {
        handleRemoveVerificationFromParams()

        return dispatch(
          buyShopOffer({
            offer: { code: offerCode } as OfferInfo,
            routerPush: push,
            snowplowPayFlow: getSnowplowPaymentFlow(),
          }),
        )
      }

      if (verificationFrom === 'account') {
        handleRemoveVerificationFromParams()

        closeLatestDialog() // close dialog before redirect

        push(ROUTES.MY_ACCOUNT)
      }
    }
  }

  return {
    handleOpenSubmitDocumentsDialogAfterClosingVerificationDialog,
    handleOpenAfterClosingSubmitDocumentsDialog,
    handleSetVerificationFromToParams,
    handleRemoveVerificationFromParams,
    getVerificationFrom,
  }
}
